/**
 * tafqeet.js — تفقيط المبالغ (تحويل الرقم إلى كلمات عربية)
 *
 * يُستخدم في الوصولات والسندات الرسمية حيث يجب كتابة المبلغ رقماً وكتابةً.
 * مثال: 1500000 → "فقط مليون وخمسمائة ألف دينار عراقي لا غير"
 */

const ONES = [
  '', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة',
  'عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر',
  'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر',
];

const TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];

const HUNDREDS = [
  '', 'مائة', 'مائتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة',
  'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة',
];

// صيغ المفرد/المثنى/الجمع لكل رتبة عددية.
// `many` (لِما فوق العشرة) بلا تنوين لأن اسم العملة يليه مباشرة في السند،
// فتُقرأ "سبعمائة وخمسون ألف دينار" لا "سبعمائة وخمسون ألفاً دينار".
const SCALES = [
  { one: 'ألف', two: 'ألفان', few: 'آلاف', many: 'ألف' },
  { one: 'مليون', two: 'مليونان', few: 'ملايين', many: 'مليون' },
  { one: 'مليار', two: 'ملياران', few: 'مليارات', many: 'مليار' },
  { one: 'ترليون', two: 'ترليونان', few: 'ترليونات', many: 'ترليون' },
];

/**
 * يحوّل صيغة المثنى إلى صيغة الإضافة بحذف النون، لأن المثنى إذا أُضيف
 * إلى ما بعده حُذفت نونه: "مائتا ألف" لا "مائتان ألف"، و"ألفا دينار"
 * لا "ألفان دينار". تُطبّق فقط حين يلي الكلمةَ اسمٌ مباشرةً (رتبة أو عملة).
 */
const toConstructState = (text) => text
  .replace(/مائتان$/, 'مائتا')
  .replace(/ألفان$/, 'ألفا')
  .replace(/مليونان$/, 'مليونا')
  .replace(/ملياران$/, 'مليارا')
  .replace(/ترليونان$/, 'ترليونا');

/** يحوّل عدداً من 1 إلى 999 إلى كلمات */
function threeDigitsToWords(n) {
  const parts = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;

  if (hundreds) parts.push(HUNDREDS[hundreds]);

  if (rest) {
    if (rest < 20) {
      parts.push(ONES[rest]);
    } else {
      const tens = Math.floor(rest / 10);
      const ones = rest % 10;
      // العربية تقدّم الآحاد على العشرات: "خمسة وعشرون"
      parts.push(ones ? `${ONES[ones]} و${TENS[tens]}` : TENS[tens]);
    }
  }

  return parts.join(' و');
}

/**
 * يحوّل عدداً صحيحاً موجباً إلى كلمات عربية.
 * @param {number} value
 * @returns {string}
 */
export function numberToArabicWords(value) {
  const num = Math.floor(Math.abs(Number(value) || 0));
  if (num === 0) return 'صفر';

  // تقسيم الرقم إلى مجموعات ثلاثية: [آحاد، آلاف، ملايين، ...]
  const groups = [];
  let remaining = num;
  while (remaining > 0) {
    groups.push(remaining % 1000);
    remaining = Math.floor(remaining / 1000);
  }

  const words = [];
  for (let i = groups.length - 1; i >= 0; i -= 1) {
    const group = groups[i];
    if (!group) continue;

    if (i === 0) {
      words.push(threeDigitsToWords(group));
      continue;
    }

    const scale = SCALES[i - 1];
    if (!scale) continue; // أكبر من المدى المدعوم

    if (group === 1) words.push(scale.one);
    else if (group === 2) words.push(scale.two);
    // اسم الرتبة يلي العدد مباشرةً، فيُضاف إليه العدد وتُحذف نون المثنى
    else if (group <= 10) words.push(`${toConstructState(threeDigitsToWords(group))} ${scale.few}`);
    else words.push(`${toConstructState(threeDigitsToWords(group))} ${scale.many}`);
  }

  // اسم العملة يلي آخر مقطع مباشرةً، فيأخذ المقطع الأخير صيغة الإضافة
  words[words.length - 1] = toConstructState(words[words.length - 1]);

  return words.join(' و');
}

/**
 * يبني عبارة التفقيط الكاملة الجاهزة للطباعة على السند.
 * @param {number} amount المبلغ
 * @param {{ name?: string, fraction?: string }} [currency] اسم العملة وكسرها
 * @returns {string} مثال: "فقط مليون وخمسمائة ألف دينار عراقي لا غير"
 */
export function tafqeet(amount, currency = { name: 'دينار عراقي', fraction: 'فلس' }) {
  const value = Number(amount) || 0;
  const isNegative = value < 0;
  const absolute = Math.abs(value);

  const whole = Math.floor(absolute);
  // تقريب الكسر لخانتين لتفادي أخطاء الفاصلة العائمة (مثل 0.29999)
  const fraction = Math.round((absolute - whole) * 100);

  const segments = [`${numberToArabicWords(whole)} ${currency.name}`];
  if (fraction > 0) {
    segments.push(`${numberToArabicWords(fraction)} ${currency.fraction}`);
  }

  const prefix = isNegative ? 'سالب ' : '';
  return `فقط ${prefix}${segments.join(' و')} لا غير`;
}

export default tafqeet;
